<template>
    <div v-dark class="quiz-result-header">
        <Icon
            v-if="isMobile"
            v-dark
            class="quiz-result-header__close-icon quiz-result-header__close-icon--mobile"
            type="close"
            title="Close"
            @click="emitClose"
        />
        <template v-if="mode">
            <Icon
                v-dark
                class="quiz-result-header__mode-icon"
                :class="{
                    'quiz-result-header__mode-icon--assignment': assignmentQuiz && assignmentName,
                }"
                :type="mode.icon"
                :style="{
                    color: isDarkMode ? mode.iconColorDM : BrandColors.ash
                }"
            />
            <h1
                v-dark
                class="quiz-result-header__mode-name"
                tabindex="-1"
            >
                <template v-if="isMockExam">
                    Mock Exam Results
                </template>
                <template v-else-if="assignmentQuiz">
                    {{ mode.name }} Results
                </template>
                <template v-else-if="isConceptQuiz">
                    {{ mode.name }} Results
                </template>
                <template v-else-if="isRecallChallengeQuiz">
                    Level Up Quiz Results
                </template>
                <template v-else>
                    {{ mode.name }} Quiz Results
                </template>
            </h1>
        </template>
        <div
            class="quiz-result-header__quiz-info"
            :class="{
                'quiz-result-header__quiz-info--assignment': assignmentQuiz && assignmentName,
            }"
        >
            <div
                v-if="dateTimeString"
                v-dark
                class="quiz-result-header__mode-bullet"
            >
                &bull;
            </div>
            <div
                v-if="dateTimeString"
                v-dark
                class="quiz-result-header__datetime"
            >
                {{ dateTimeString }}
            </div>
            <div
                v-if="levelUpQuizLevel && numOfLevelsInSubject"
                v-dark
                class="quiz-result-header__level-up-info"
            >
                <div
                    v-dark
                    class="quiz-result-header__mode-bullet"
                    :class="{
                        'quiz-result-header__mode-bullet--level-up': levelUpQuizLevel && numOfLevelsInSubject,
                    }"
                >
                    &bull;
                </div>
                <div
                    class="quiz-result-header__level-up-quiz-info"
                    :class="{
                        'quiz-result-header__level-up-quiz-info--level-dark-mode': isDarkMode,
                    }"
                >
                    Level {{ levelUpQuizLevel }}/{{ numOfLevelsInSubject }}
                </div>
            </div>
            <div
                v-if="isRecallChallengeQuiz"
                v-dark
                class="quiz-result-header__recall-challenge-info"
            >
                <div
                    v-dark
                    class="quiz-result-header__mode-bullet"
                    :class="{
                        'quiz-result-header__mode-bullet--recall-challenge': isRecallChallengeQuiz,
                    }"
                >
                    &bull;
                </div>
                <div
                    v-dark
                    class="quiz-result-header__recall-challenge-quiz-info"
                    :class="{
                        'quiz-result-header__recall-challenge-info--recall-challenge-dark-mode': isDarkMode,
                    }"
                >
                    Recall Challenge
                </div>
            </div>
            <div
                v-if="assignmentQuiz && assignmentName"
                v-dark
                class="quiz-result-header__assignment-info"
            >
                <div
                    v-dark
                    class="quiz-result-header__mode-bullet"
                    :class="{
                        'quiz-result-header__mode-bullet--assignment': assignmentQuiz && assignmentName,
                    }"
                >
                    &bull;
                </div>
                <div
                    class="quiz-result-header__assignment-quiz-info"
                    :class="{
                        'quiz-result-header__assignment-quiz-info--assignment-dark-mode': isDarkMode,
                    }"
                >
                    {{ assignmentName }}
                </div>
            </div>
        </div>
        <div v-if="!isLoading" class="quiz-result-header__actions">
            <div class="quiz-result-header__actions">
                <template v-if="mode && mode.shortName === 'levelUp' && showHeaderActions">
                    <PocketButton
                        class="quiz-result-header__secondary-btn"
                        type="secondary"
                        :is-dark-mode="isDarkMode"
                        @click="emitClose"
                    >
                        Close Results
                    </PocketButton>
                    <PocketButton
                        type="primary"
                        :is-dark-mode="isDarkMode"
                        @click="emitGoToLevelUp"
                    >
                        Keep Studying
                    </PocketButton>
                </template>
                <template v-else-if="showHowYouHeardAboutUsCta">
                    <PocketButton
                        class="quiz-result-header__secondary-btn"
                        type="secondary"
                        :is-dark-mode="isDarkMode"
                        @click="emitClose"
                    >
                        Close Results
                    </PocketButton>
                    <PocketButton
                        type="primary"
                        :is-dark-mode="isDarkMode"
                        @click="emitGoToHowDidYouHearAboutUs"
                    >
                        Continue
                    </PocketButton>
                </template>
                <template v-else>
                    <PocketButton
                        v-if="quiz && canRetakeQuiz && !isMockExam"
                        class="quiz-result-header__secondary-btn"
                        type="secondary"
                        :is-dark-mode="isDarkMode"
                        @click="emitRetake"
                    >
                        Retake Quiz
                    </PocketButton>
                    <Icon
                        v-if="!canRetakeQuiz || isMockExam"
                        type="close"
                        class="quiz-result-header__close-icon"
                        :is-dark-mode="isDarkMode"
                        @click="emitClose"
                        v-dark
                    />
                    <PocketButton
                        v-else
                        type="primary"
                        :is-dark-mode="isDarkMode"
                        @click="emitClose"
                    >
                        Close Results
                    </PocketButton>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { userModule } from '@/store/user/module'
import { months } from '@/utils'
import type { Study } from '@pocketprep/types'
import UIKit, { utils } from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import { screenModule } from '@/store/screen/module'

const {
    Icon,
    Button: PocketButton,
} = UIKit

const props = withDefaults(
    defineProps<{
        canRetakeQuiz: boolean
        quiz?: Study.Class.QuizJSON | null
        levelUpQuizLevel?: number
        numOfLevelsInSubject?: number
        showHowYouHeardAboutUsCta?: boolean
        isLoading?: boolean
        showHeaderActions?: boolean
    }>(),
    {
        quiz: null,
        levelUpQuizLevel: undefined,
        numOfLevelsInSubject: undefined,
        showHowYouHeardAboutUsCta: false,
        isLoading: false,
        showHeaderActions: true,
    }
)

const emit = defineEmits<{
    close: []
    retake: []
    goToLevelUp: []
    goToHeardAboutUsAsk: []
}>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const isMobile = computed(() => {
    return breakpoint.value === 'black-bear'
})

const mode = computed(() => {
    if (!props.quiz) {
        return null
    }
    const code = String(props.quiz.mode)
    return utils.studyModes[(code === '1' ? '0' : code) as keyof typeof utils.studyModes]
})

const isMockExam = computed(() => {
    return props.quiz?.mode === 5
})

const isConceptQuiz = computed(() => {
    return props.quiz?.mode === 12
})

const isRecallChallengeQuiz = computed(() => {
    return props.quiz?.mode === 7
})

const createdAt = computed(() => {
    if (!props.quiz) {
        return null
    }
    return new Date(props.quiz.createdAt)
})

const dateTimeString = computed(() => {
    if (!createdAt.value) {
        return null
    }

    const dateString = `${months[createdAt.value.getMonth()]?.slice(0, 3)} ${createdAt.value.getDate()}`
    const hours = createdAt.value.getHours()
    const minutes = `0${createdAt.value.getMinutes()}`.slice(-2)
    const timeString = `${(hours % 12) || 12}:${minutes}${hours >= 12 ? 'pm' : 'am'}`
    return `${dateString} at ${timeString}`
})

const assignmentQuiz = computed(() => {
    return props.quiz?.prebuiltQuiz as Study.Class.PrebuiltQuizJSON
})

const assignmentName = computed(() => {
    return assignmentQuiz.value?.name ? assignmentQuiz.value.name : null
})

const emitClose = () => {
    emit('close')
}

const emitRetake = () => {
    emit('retake')
}

const emitGoToLevelUp = () => {
    emit('goToLevelUp')
}

const emitGoToHowDidYouHearAboutUs = () => {
    emit('goToHeardAboutUsAsk')
}
</script>

<style lang="scss" scoped>
.quiz-result-header {
    box-sizing: border-box;
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 64px;
    border-bottom: 1px solid $gray-divider;
    padding: 0 22px;
    background-color: $white;
    z-index: 3;

    &--dark {
        background-color: $charcoal;
        border-bottom-color: $jet;

        @include breakpoint(black-bear) {
            background-color: $brand-black;
        }
    }

    @include breakpoint(brown-bear) {
        padding-left: 30px;
        padding-right: 30px;
    }

    @include breakpoint(black-bear) {
        flex-direction: column;
        height: auto;
        border-bottom: none;
        padding: 25px 0 0 0;
    }

    &__close-icon {
        z-index: 4;
        color: $brand-blue;
        width: 24px;
        height: 24px;
        cursor: pointer;

        &:hover {
            color: $brand-black;
        }

        &--mobile {
            position: absolute;
            top: 20px;
            left: 15px;
            width: 30px;
            height: 30px;
        }

        &--dark {
            color: $banana-bread;

            &:hover {
                color: $butterscotch;
            }
        }
    }

    &__mode-icon {
        margin-right: 12px;
        margin-bottom: 3px;

        @include breakpoint(black-bear) {
            margin-right: 0;
            margin-bottom: 17px;
        }

        &--assignment {
            margin-bottom: 2px;
            width: 22px;
            height: 21px;

            @include breakpoint(black-bear) {
                margin-right: 0;
            }

            &--dark {
                color: $fog !important;
            }
        }
    }

    &__mode-name {
        display: flex;
        align-items: center;
        font-weight: 600;
        font-size: 16px;
        letter-spacing: 0.18px;
        line-height: 22px;
        outline: none;

        &--dark {
            color: $white;
        }

        @include breakpoint(black-bear) {
            margin-right: 0;
            margin-bottom: 4px;
            font-size: 17px;
            letter-spacing: 0.23px;
            line-height: 20px;
        }
    }

    &__quiz-info {
        display: flex;
        align-items: center;

        @include breakpoint(black-bear) {
            align-items: flex-start;
        }

        &--assignment {
            @include breakpoint(black-bear) {
                display: flex;
                flex-direction: column-reverse;
                align-items: center;
            }
        }
    }

    &__mode-bullet {
        font-size: 16px;
        color: $ash;
        margin: 0 12px;

        &--dark {
            color: $fog;
        }

        @include breakpoint(brown-bear) {
            margin: 0 8px;
        }

        @include breakpoint(black-bear) {
            display: none;
        }

        &--level-up,
        &--recall-challenge {
            @include breakpoint(black-bear) {
                margin: 2px 8px;
                display: block;
            }
        }

        &--assignment {
            @include breakpoint(black-bear) {
                display: none;
            }
        }
    }


    &__level-up-info,
    &__recall-challenge-info {
        display: flex;
        align-items: center;

        @include breakpoint(black-bear) {
            align-items: flex-start;
        }
    }

    &__assignment-info {
        display: flex;
        align-items: center;
    }

    &__datetime,
    &__level-up-quiz-info,
    &__recall-challenge-quiz-info,
    &__assignment-quiz-info {
        color: $ash;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;

        @include breakpoint(black-bear) {
            height: 20px;
            letter-spacing: 0.32px;
            font-size: 15px;
            font-weight: 500;
            line-height: 20px;
            margin-bottom: 10px;
        }

        &--dark {
            color: $fog;
        }

        &--level-dark-mode,
        &--recall-challenge-dark-mode,
        &--assignment-dark-mode {
            color: white
        }
    }

    &__assignment-quiz-info {
        height: auto;

        @include breakpoint(black-bear) {
            width: 248px;
            margin-bottom: 4px;
            text-align: center;
        }
    }

    &__actions {
        display: flex;
        height: 100%;
        align-items: center;
        margin-left: auto;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__secondary-btn {
        margin-right: 4px;
    }
}
</style>
